<template>
  <div class="eventoFloatingPlayer" :class="'tipo-' + servidorTipo">
    <div v-show="servidorTipo == 'openvidu'" ref="openvidu"></div>
    <video
      v-show="servidorTipo == 'ant'"
      :id="antVideoId"
      ref="antVideo"
      autoplay
      controls
      playsinline
      :muted="muted"
    ></video>
    <div v-show="servidorTipo == 'sh'" ref="flashContainer">
      <video ref="flashVideo" autoplay controls playsinline :muted="muted"></video>
    </div>
    <video
      v-show="servidorTipo == 'ms'"
      ref="msVideo"
      autoplay
      controls
      playsinline
      :muted="muted"
    ></video>

    <div v-if="estado !== 'playing'" class="eventoFloatingPlayerStatus">
      <i
        v-if="estado !== 'error'"
        class="fa fa-circle-o-notch fa-spin"
        aria-hidden="true"
      ></i>
      <i v-else class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      <span>{{ mensagemEstado }}</span>
    </div>
  </div>
</template>

<script>
import Flashphoner from "@flashphoner/websdk/flashphoner.js";
import { OpenVidu } from "openvidu-browser";
import { api } from "@/services/api.js";
import { WebRTCAdaptor } from "@/libs/webrtc_adaptor.js";
import MediasoupSubscriber from "@/services/MediasoupSubscriber.js";

export default {
  name: "EventoFloatingPlayer",
  props: {
    eventoId: { type: String, required: true },
    canal: { type: [String, Number], default: "" },
    muted: { type: Boolean, default: true },
  },
  data() {
    return {
      item: null,
      estado: "loading",
      antVideoId:
        "multiAntVideo_" +
        this.eventoId.replace(/[^a-zA-Z0-9_-]/g, "") +
        "_" +
        Math.random().toString(36).slice(2),
      openviduSession: null,
      antAdaptor: null,
      flashSession: null,
      flashStream: null,
      msSubscriber: null,
      sessionViewInterval: null,
    };
  },
  computed: {
    servidorTipo() {
      return this.item ? this.item.servidorTipo || "openvidu" : "";
    },
    mensagemEstado() {
      if (this.estado === "error") {
        return "Não foi possível carregar este evento.";
      }
      if (this.estado === "waiting") {
        return "Aguardando transmissão...";
      }
      if (this.estado === "reconnecting") {
        return "Reconectando transmissão...";
      }
      return "Carregando transmissão...";
    },
  },
  watch: {
    muted() {
      this.aplicarAudio();
    },
  },
  mounted() {
    this.carregar();
  },
  beforeDestroy() {
    this.encerrar();
  },
  methods: {
    async carregar() {
      try {
        let parametros = "";
        if (this.canal !== "" && this.canal !== null) {
          parametros = "?canal=" + encodeURIComponent(this.canal);
        }
        const response = await api.get(
          "/evento/view/" + this.eventoId + parametros
        );

        if (
          response.data.erro ||
          !response.data.itens ||
          !response.data.itens.length
        ) {
          this.estado = "error";
          return;
        }

        this.item = response.data.itens[0];
        this.$emit("loaded", this.item);
        await this.$nextTick();
        this.iniciarPlayer();
        this.sessionViewInterval = setInterval(this.renovarSessao, 15000);
      } catch (error) {
        this.estado = "error";
      }
    },
    iniciarPlayer() {
      if (this.servidorTipo === "openvidu") {
        this.iniciarOpenVidu();
      } else if (this.servidorTipo === "ant") {
        this.iniciarAnt();
      } else if (this.servidorTipo === "sh") {
        this.iniciarFlashphoner();
      } else if (this.servidorTipo === "ms") {
        this.iniciarMediasoup();
      } else {
        this.estado = "error";
      }
    },
    iniciarOpenVidu() {
      const openvidu = new OpenVidu();
      openvidu.enableProdMode();
      this.openviduSession = openvidu.initSession();
      this.openviduSession.on("streamCreated", (event) => {
        const subscriber = this.openviduSession.subscribe(
          event.stream,
          this.$refs.openvidu
        );
        subscriber.on("videoElementCreated", ({ element }) => {
          element.controls = true;
          element.playsInline = true;
          element.muted = this.muted;
          this.estado = "playing";
        });
      });

      const data = {};
      if (this.item.linkFonte && this.item.nome.indexOf("#") > -1) {
        data.clientData = "Client_" + this.item.nome.split("#")[1];
      }
      this.openviduSession.connect(this.item.ovToken, data).catch(() => {
        this.estado = "error";
      });
    },
    iniciarAnt() {
      const streamId = this.item.idCanal;
      const websocketURL =
        this.item.servidor.replace("https://", "wss://") +
        ":5443/WebRTCAppEE/websocket";
      this.antAdaptor = new WebRTCAdaptor({
        websocket_url: websocketURL,
        mediaConstraints: { video: false, audio: false },
        peerconnection_config: {
          iceServers: [{ urls: "stun:stun1.l.google.com:19302" }],
        },
        sdp_constraints: {
          OfferToReceiveAudio: true,
          OfferToReceiveVideo: true,
        },
        remoteVideoId: this.antVideoId,
        isPlayMode: true,
        debug: false,
        candidateTypes: ["tcp", "udp"],
        callback: (info) => {
          if (info === "initialized") {
            this.antAdaptor.play(
              streamId,
              this.item.antToken,
              "",
              [],
              "",
              ""
            );
          } else if (info === "play_started") {
            this.estado = "playing";
            this.aplicarAudio();
          } else if (info === "play_finished") {
            this.estado = "waiting";
          }
        },
        callbackError: () => {
          this.estado = "error";
        },
      });
    },
    iniciarFlashphoner() {
      Flashphoner.init({});
      const SESSION_STATUS = Flashphoner.constants.SESSION_STATUS;
      this.flashSession = Flashphoner.createSession({
        urlServer: this.item.servidor,
        custom: { token: this.item.key },
      })
        .on(SESSION_STATUS.ESTABLISHED, (session) => {
          this.flashStream = session.createStream({
            name: this.item._id + "_" + this.item.canal,
            display: this.$refs.flashContainer,
            remoteVideo: this.$refs.flashVideo,
            receiveAudio: true,
            custom: { token: this.item.key },
          });
          this.flashStream.play();
          this.estado = "playing";
          this.aplicarAudio();
        })
        .on(SESSION_STATUS.FAILED, () => {
          this.estado = "error";
        });
    },
    iniciarMediasoup() {
      this.msSubscriber = new MediasoupSubscriber({
        url: this.item.servidor,
        token: this.item.msToken,
        video: this.$refs.msVideo,
        onState: (estado) => {
          this.estado = estado;
          this.aplicarAudio();
        },
      });
      this.msSubscriber.connect();
    },
    aplicarAudio() {
      this.$el.querySelectorAll("video").forEach((video) => {
        video.muted = this.muted;
      });
    },
    async renovarSessao() {
      if (!this.item || !this.item.sessaoView) {
        return;
      }
      try {
        await api.put("/sessaoView", { token: this.item.sessaoView });
      } catch (error) {
        // A próxima renovação tentará novamente.
      }
    },
    encerrar() {
      clearInterval(this.sessionViewInterval);
      if (this.openviduSession) {
        this.openviduSession.disconnect();
      }
      if (this.antAdaptor) {
        try {
          this.antAdaptor.stop(this.item.idCanal);
          this.antAdaptor.closeWebSocket();
        } catch (error) {
          // A conexão pode já estar encerrada.
        }
      }
      if (this.flashStream) {
        try {
          this.flashStream.stop();
        } catch (error) {
          // O stream pode já estar encerrado.
        }
      }
      if (this.flashSession && this.flashSession.disconnect) {
        this.flashSession.disconnect();
      }
      if (this.msSubscriber) {
        this.msSubscriber.disconnect();
      }
      this.$el.querySelectorAll("video").forEach((video) => {
        video.pause();
        video.srcObject = null;
      });
    },
  },
};
</script>

<style scoped>
.eventoFloatingPlayer {
  background: #000000;
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.eventoFloatingPlayer > div:not(.eventoFloatingPlayerStatus),
.eventoFloatingPlayer > div:not(.eventoFloatingPlayerStatus) > div,
.eventoFloatingPlayer video {
  height: 100%;
  width: 100%;
}

.eventoFloatingPlayer video {
  background: #000000;
  display: block;
  object-fit: contain;
}

.eventoFloatingPlayerStatus {
  align-items: center;
  background: #090d0f;
  color: #d9e0e3;
  display: flex;
  font-size: 11px;
  gap: 8px;
  inset: 0;
  justify-content: center;
  position: absolute;
}

.eventoFloatingPlayerStatus i {
  color: #35b37f;
}
</style>
